import { useRef, useMemo, useEffect, useImperativeHandle, useCallback } from 'react';
import { FocusScope, useControlledState, useFocusManager, useFocusWithin, useInteractOutside, isElementInChildOfActiveScope } from '@sail/react-aria';
import * as React from 'react';
import { useFloatingNodeId, useInteractions, useRole, useDismiss, useClick, useFocus, useHover, safePolygon, FloatingNode, useFloatingParentNodeId, FloatingTree } from '@floating-ui/react';
import { createView, view, assignProps, dynamic, animate, AnimatePresence, toIntent } from '@sail/engine';
import { tokens } from '@sail/tokens';
import { css } from '../../css';
import { Layer } from '../layers/Layer';
import { toFloatingPlacement } from '../utils/toFloatingPlacement';
import { useProps } from '../../hooks/useProps';
import { replaceDeprecatedProps, moveFocus, FocusGuard } from './utils';
import { usePopoverIframe } from '../../iframe-overrides/PopoverOverrides';
import { useUiConfig } from '../../providers/UiConfigProvider';
import { useFloating } from './useFloating';
const iframeOverrides = toIntent([
    assignProps(usePopoverIframe),
    assignProps({
        shouldCloseOnBlur: false
    }),
    css({
        backgroundColor: 'surface',
        border: '1px solid',
        borderColor: 'border',
        borderRadius: 'medium',
        boxShadow: 'overlay',
        maxHeight: 'inherit'
    })
]);
/**
 * Popover option props have either a boolean value, or an object with options.
 * This helper function returns a tuple of the boolean value indicating whether the option is enabled,
 * and the option object itself, defaulting to `{}` if not provided.
 */ const option = (value)=>{
    if (value === undefined) {
        return [
            undefined,
            {}
        ];
    }
    if (typeof value === 'boolean') {
        return [
            value,
            {}
        ];
    }
    return [
        true,
        value
    ];
};
/**
 * A portal-friendly version of react-aria's `useInteractOutside` hook, allowing
 * the popover to be dismissed when focus moves outside the popover.
 *
 * `useInteractOutside` uses DOM event delegation to detect clicks outside the popover,
 * but this isn't safe for portalled elements because the event target may not be
 * a direct child of the popover.
 *
 * This hook uses the React tree (instead of the DOM tree) to detect when focus moves
 * outside the popover.
 */ const useFocusOutside = (callback, targetElement)=>{
    const focusMovedRef = useRef(false);
    const timeoutRef = React.useRef();
    useEffect(()=>()=>cancelAnimationFrame(timeoutRef.current), []);
    return useMemo(()=>({
            onFocus () {
                focusMovedRef.current = true;
            },
            // blur on the current element fires before focus on the next element,
            // so we use that to determine if the focus is moving outside the popover
            onBlur (e) {
                var _e_currentTarget;
                focusMovedRef.current = false;
                // Ignore blur events default prevented by children
                if (e.defaultPrevented) {
                    return;
                }
                cancelAnimationFrame(timeoutRef.current);
                const relatedTarget = e.relatedTarget;
                var _e_currentTarget_contains;
                const isFocusWithinCurrentTarget = (_e_currentTarget_contains = (_e_currentTarget = e.currentTarget) === null || _e_currentTarget === void 0 ? void 0 : _e_currentTarget.contains(relatedTarget)) !== null && _e_currentTarget_contains !== void 0 ? _e_currentTarget_contains : false;
                var _targetElement_contains;
                /**
         * In some cases trigger element might not be inside the current target
         */ const isFocusWithinTargetElement = (_targetElement_contains = targetElement === null || targetElement === void 0 ? void 0 : targetElement.contains(relatedTarget)) !== null && _targetElement_contains !== void 0 ? _targetElement_contains : false;
                const isFocusWithin = isFocusWithinCurrentTarget || isFocusWithinTargetElement;
                /**
         * preventDefault onBlur event if focus is within targeted elements,
         * so parent Popovers don't close on internal focus changes
         */ if (isFocusWithin) {
                    e.preventDefault();
                }
                // Give browser time to focus the next element
                timeoutRef.current = requestAnimationFrame(()=>{
                    // Check if the new focused element is a child of the original container
                    if (!focusMovedRef.current && !isFocusWithin) {
                        callback();
                    }
                });
            }
        }), [
        callback,
        targetElement
    ]);
};
const useReactAriaFocusOutside = (context, { enabled = false })=>{
    const { refs, open: isOpen, onOpenChange, elements: { domReference, floating } } = context;
    const isElementWithin = useCallback((element)=>(domReference === null || domReference === void 0 ? void 0 : domReference.contains(element)) || (floating === null || floating === void 0 ? void 0 : floating.contains(element)), [
        domReference,
        floating
    ]);
    /**
   * This ref keeps track of whether focus is within the overlay or not.
   * It needs to prevent the overlay from closing when focus moves to a child focus scope.
   * Example: Popover -> Popover -> Button, moving focus to Button should not close the popover.
   */ const isFocusWithinRef = useRef(false);
    const timeoutRef = useRef();
    useEffect(()=>()=>cancelAnimationFrame(timeoutRef.current), []);
    const onInteractOutside = useCallback((e)=>{
        /**
       * Do not close the overlay if focus is within the overlay.
       */ if (isFocusWithinRef.current) {
            return;
        }
        const isElementWithinPopover = isElementWithin(e.target);
        if (!isElementWithinPopover) {
            onOpenChange(false, e.nativeEvent);
        }
    }, [
        isElementWithin,
        onOpenChange
    ]);
    // Handle clicking outside the overlay to close it
    useInteractOutside({
        ref: refs.floating,
        onInteractOutside: enabled && isOpen ? onInteractOutside : undefined
    });
    const { focusWithinProps } = useFocusWithin({
        isDisabled: !enabled,
        onFocusWithinChange: (isFocusWithin)=>{
            isFocusWithinRef.current = isFocusWithin;
        },
        onBlurWithin: (e)=>{
            const relatedTarget = e.relatedTarget;
            // Do not close if relatedTarget is null, which means focus is lost to the body.
            // That can happen when switching tabs, or due to a VoiceOver/Chrome bug with Control+Option+Arrow navigation.
            // Clicking on the body to close the overlay should already be handled by useInteractOutside.
            // https://github.com/adobe/react-spectrum/issues/4130
            // https://github.com/adobe/react-spectrum/issues/4922
            //
            // If focus is moving into a child focus scope (e.g. menu inside a dialog),
            // do not close the outer overlay. At this point, the active scope should
            // still be the outer overlay, since blur events run before focus.
            if (!relatedTarget || isElementInChildOfActiveScope(relatedTarget)) {
                return;
            }
            isFocusWithinRef.current = false;
            cancelAnimationFrame(timeoutRef.current);
            const isRelatedTargetWithin = isElementWithin(relatedTarget);
            timeoutRef.current = requestAnimationFrame(()=>{
                const isFocusWithin = isFocusWithinRef.current;
                if (!isFocusWithin && !isRelatedTargetWithin) {
                    onOpenChange(false, e.nativeEvent);
                }
            });
        }
    });
    return useMemo(()=>{
        if (!enabled || !isOpen) {
            return {};
        }
        return {
            floating: focusWithinProps,
            reference: focusWithinProps
        };
    }, [
        enabled,
        isOpen,
        focusWithinProps
    ]);
};
const translate = (placement)=>{
    /* eslint-disable no-nested-ternary */ const [attachedSide] = placement.split('-');
    const offset = tokens['space.150'];
    const negativeOffset = `calc(-1 * ${offset})`;
    return `translate(${attachedSide === 'left' ? offset : attachedSide === 'right' ? negativeOffset : '0'}, ${attachedSide === 'top' ? offset : attachedSide === 'bottom' ? negativeOffset : '0'})`;
};
const ANIMATION_EASING = 'cubic-bezier(0, .09, .4, 1)';
const ANIMATION_DURATION_MEDIUM = 150;
const ANIMATION_DURATION_SHORT = 100;
const POPOVER_PADDING = 4;
const DesktopPopoverLayer = createView((allProps)=>{
    var _middlewareData_hide;
    const { targetRef, placement: reactAriaPlacement, offset: offsetOptions, flip: flipEnabled, shift: shouldShift, size: sizeOptions, inline: inlineEnabled, hide: hideOptions, onClose, subviews, dismissible, showOnPress, showOnFocus, showOnHover, role, defaultOpen, open: controlledOpen, mobileTitle, onOpenChange: setControlledOpen, floatingStrategy, animate: shouldAnimate = true, useReactAriaDismissOutside = false, ...props } = replaceDeprecatedProps(allProps);
    const [open, setOpen] = useControlledState(// Here we default the controlled state to `true` if neither `open` nor `defaultOpen` are provided.
    // This provides backwards compatibility by continuing to call `onClose` events but not automatically
    // hiding the popover. This feels a little odd as you might reasonably expect the popover to be
    // uncontrolled by default, but it's the best we can do without breaking backwards compatibility. The
    // drawback is that you must explicitly provide the `defaultOpen` prop if you want the popover to be
    // uncontrolled.
    controlledOpen !== null && controlledOpen !== void 0 ? controlledOpen : defaultOpen === undefined ? true : undefined, defaultOpen, setControlledOpen);
    const placement = toFloatingPlacement(reactAriaPlacement);
    const [shiftEnabled, shiftOptions] = option(shouldShift);
    // eslint-disable-next-line prefer-const
    let [dismissEnabled, dismissOptions] = option(dismissible);
    const [clickEnabled, clickOptions] = option(showOnPress);
    const [focusEnabled, focusOptions] = option(showOnFocus);
    const [hoverEnabled, hoverOptions] = option(showOnHover);
    const onOpenChange = useCallback((isOpen)=>{
        setOpen(isOpen);
        if (!isOpen) {
            // backwards compatibility for onClose
            onClose === null || onClose === void 0 ? void 0 : onClose();
        }
    }, [
        setOpen,
        onClose
    ]);
    const nodeId = useFloatingNodeId();
    const floating = useFloating({
        nodeId,
        placement,
        floatingStrategy,
        offsetValue: offsetOptions !== null && offsetOptions !== void 0 ? offsetOptions : POPOVER_PADDING,
        inlineEnabled,
        flipEnabled,
        hideOptions,
        shiftEnabled: shiftEnabled !== false,
        shiftOptions,
        sizeOptions,
        open,
        onOpenChange
    });
    const { refs, floatingStyles, context, middlewareData } = floating;
    const scope = useRef(null);
    const shouldDismissOnFocusOutside = dismissEnabled && dismissOptions.outsideFocus !== false;
    const dismissOnFocusOutside = useFocusOutside(useCallback(()=>{
        if (!useReactAriaDismissOutside && shouldDismissOnFocusOutside) {
            onOpenChange(false);
        }
    }, [
        onOpenChange,
        shouldDismissOnFocusOutside,
        useReactAriaDismissOutside
    ]), targetRef.current);
    const interactions = useInteractions([
        useRole(context, {
            enabled: Boolean(role),
            role
        }),
        useDismiss(context, {
            enabled: dismissEnabled,
            escapeKey: dismissOptions.escapeKey,
            outsidePress: dismissOptions.outsidePress,
            // outsidePressEvent needs to be set to 'click' to ensure compatibility with
            // react-aria's Pressable implementation.
            outsidePressEvent: 'click',
            bubbles: dismissOptions.bubbles
        }),
        useClick(context, {
            enabled: clickEnabled,
            toggle: clickOptions.toggle
        }),
        useFocus(context, {
            enabled: focusEnabled,
            keyboardOnly: focusOptions.visibleOnly
        }),
        useHover(context, {
            enabled: hoverEnabled,
            delay: hoverOptions.delay,
            restMs: hoverOptions.restMs,
            handleClose: safePolygon()
        }),
        // wire up the reference element to dismiss the popover when it loses focus.
        // This logic is only necessary because the `useDismiss` hook relies on
        // event delegation and react-aria "pressable" elements don't propagate mouse/touch events.
        {
            reference: dismissOnFocusOutside,
            floating: dismissOnFocusOutside
        },
        useReactAriaFocusOutside(context, {
            enabled: useReactAriaDismissOutside && shouldDismissOnFocusOutside
        })
    ]);
    useProps(targetRef, interactions.getReferenceProps());
    // set up refs for the trigger element for floating-ui
    useEffect(()=>{
        // floating UI will call `calculatePosition` as long as it has both a floating and reference element
        // we can clear this reference (to avoid calculating position) if switching to mobile
        refs.setReference(targetRef.current);
    }, [
        targetRef,
        refs
    ]);
    const ref = React.useRef(null);
    const layer = /*#__PURE__*/ React.createElement(Layer, {
        inherits: subviews.layer,
        uses: [
            assignProps(interactions.getFloatingProps())
        ],
        ref: (el)=>{
            // don't run positioning logic within JSDOM environment (speeds up tests)
            // JSDOM doesn't support `getBoundingClientRect` anyway, so positioning logic
            // wouldn't be correct there anyway.
            if (typeof jest === 'undefined') {
                refs.setFloating(el);
            }
        },
        style: floatingStyles,
        css: (hideOptions === null || hideOptions === void 0 ? void 0 : hideOptions.referenceHidden) && ((_middlewareData_hide = middlewareData.hide) === null || _middlewareData_hide === void 0 ? void 0 : _middlewareData_hide.referenceHidden) ? css({
            visibility: 'hidden'
        }) : undefined
    }, /*#__PURE__*/ React.createElement(FloatingNode, {
        id: nodeId
    }, /*#__PURE__*/ React.createElement(FocusScope, {
        restoreFocus: true
    }, /*#__PURE__*/ React.createElement(view.div, {
        // declare this before spreading props so user's test id wins out, but we can
        // still use this test id for sail next tests
        "data-testid": "popover-layer",
        ...props,
        css: (sizeOptions === null || sizeOptions === void 0 ? void 0 : sizeOptions.maxHeight) ? css({
            overflow: 'auto'
        }) : undefined,
        ref: ref,
        uses: [
            dynamic(()=>{
                const manager = useFocusManager();
                useImperativeHandle(scope, ()=>manager);
            }),
            ...shouldAnimate ? [
                animate.in({
                    opacity: 0,
                    transform: `scale(0.95) ${translate(floating.placement)}`,
                    easing: ANIMATION_EASING,
                    duration: ANIMATION_DURATION_MEDIUM
                }),
                animate.out({
                    opacity: 0,
                    transform: `scale(0.95) ${translate(floating.placement)}`,
                    easing: ANIMATION_EASING,
                    duration: ANIMATION_DURATION_SHORT
                })
            ] : [],
            css({
                maxHeight: 'inherit'
            })
        ]
    }, props.children))));
    return /*#__PURE__*/ React.createElement(React.Fragment, null, open ? /*#__PURE__*/ React.createElement(FocusGuard, {
        onFocus: moveFocus(scope, 'next')
    }) : null, /*#__PURE__*/ React.createElement(AnimatePresence, null, context.open ? layer : null), open ? /*#__PURE__*/ React.createElement(FocusGuard, {
        onFocus: moveFocus(scope, 'prev')
    }) : null);
}, {
    css: {
        backgroundColor: 'surface',
        border: '1px solid',
        borderColor: 'border',
        borderRadius: 'medium',
        boxShadow: 'overlay',
        maxHeight: 'inherit'
    }
});
export const DesktopPopover = createView((props)=>{
    const uiConfig = useUiConfig();
    const parentId = useFloatingParentNodeId();
    const shouldApplyIframeOverrides = !!(uiConfig === null || uiConfig === void 0 ? void 0 : uiConfig._unstable_requestIframe);
    const intents = shouldApplyIframeOverrides ? iframeOverrides : undefined;
    const popover = /*#__PURE__*/ React.createElement(DesktopPopoverLayer, {
        ...props,
        uses: intents
    });
    // If there is no ancestor popover, wrap the popover in a FloatingTree
    // so that nested popovers can coordinate dismissal behavior.
    return parentId === null ? /*#__PURE__*/ React.createElement(FloatingTree, null, popover) : popover;
});

//# sourceMappingURL=Popover.desktop.js.map